import React, { useEffect, useState } from 'react';
import { MyTrainingsViewContext } from './MyTrainingsViewContext';
import { UserWorkspaceProfile } from 'models/UserWorkspaceProfile';
import { useLocation, useNavigate } from 'react-router';
import { useGetProfile } from 'hooks/useGetProfile';
import { useGetMyTrainingInfo } from 'hooks/useGetMyTrainingInfo';
import { UserLessonStatistics } from 'models/UserLessonStatistics';
import { useAuthContext } from 'hooks/useAuthContext';
import { useGetUserProfilePhoto } from 'hooks/useGetUserProfilePhoto';
import { RoutePaths } from 'constants/RoutePaths';

interface MyTrainingsViewContextProviderProps {
    children: React.ReactNode;
}

export const MyTrainingsViewContextProvider: React.FC<MyTrainingsViewContextProviderProps> = ({ children }) => {
    const { userState } = useAuthContext();
    const [user, setUser] = useState<UserWorkspaceProfile | undefined>(undefined);
    const [trainingStatistics, setTrainingStatistics] = useState<UserLessonStatistics | null>(null);
    const [isStatisticOfLoggedUser, setIsStatisticOfLoggedUser] = useState<boolean>(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { getProfile, getProfilePending } = useGetProfile();
    const { getUserProfilePhotoUrl } = useGetUserProfilePhoto();
    const { getMyTrainingStatisticsApiCall, getStatisticsPending} = useGetMyTrainingInfo();

    const queryParams = new URLSearchParams(location.search);
    const userId = queryParams.get('userId');  

    const fetchData = async () => {
      if (userState.user.userId) {
        if (userId && userId !== userState.user.userId) {
          const getProfileResp = await getProfile(userId);
          if (getProfileResp?.status === 200) {
            const profileData = getProfileResp.data;
            const profilePhotoUrl = await getUserProfilePhotoUrl(userId);
            const profile = !profileData ? undefined : { ...profileData, profilePhotoUrl } ;
            setUser(profile ?? undefined);
    
            getMyTrainingStatisticsApiCall(userId)
            .then(response => {
                setTrainingStatistics(response ?? null);
            });
    
            setIsStatisticOfLoggedUser(false);
          } else if (getProfileResp?.status === 403) {
            navigate(RoutePaths.UNAUTHORIZED);
          }
        } else {
          setUser({
            id: userState.user.userId,
            displayName: userState.user.selectedWorkspace.userDisplayName,
            email: userState.user.email,
            emailConfirmed: userState.user.emailConfirmed,
            profilePhotoUrl: userState.user.selectedWorkspace.userPhotoUrl,
            role: userState.user.selectedWorkspace.userRole,
            hasLessons: userState.user.selectedWorkspace.hasUserLessons,
            hasSubordinates: userState.user.selectedWorkspace.hasUserSubordinates,
            workspace: {
              id: userState.user.selectedWorkspace.workspaceId,
              name: userState.user.selectedWorkspace.workspaceName
            },
            patrons: userState.user.selectedWorkspace.userSuperiors
          });
          getMyTrainingStatisticsApiCall(undefined)
          .then(response => {
              setTrainingStatistics(response ?? null);
          });
          setIsStatisticOfLoggedUser(true);
        }
      }
    }

    useEffect(()=> {
        fetchData();
    }, [userState.user, userState.user.userId, userId, getProfile, getMyTrainingStatisticsApiCall]);

    return (
        <MyTrainingsViewContext.Provider value={{ trainingStatistics, isStatisticOfLoggedUser, user, getStatisticsPending }}>
          {children}
        </MyTrainingsViewContext.Provider>
      );
}